import { getLanguageName } from "@/utils/language";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User } from "snudbud-backbone-sdk";
import { useAPI } from "../contexts/APIContext";
import handleError from "../utils/handleError";

export default function OnboardingComplete() {
  const { api, user, userProfile } = useAPI();
  const [learningLanguages, setLearningLanguages] = useState<User.FetchProfileReturn["languages"]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.userId) return;

    // Use cached profile or fetch it if not available
    setIsLoading(true);

    const loadProfile = async () => {
      try {
        let profile;
        if (userProfile) {
          profile = userProfile;
        } else {
          profile = await api.user.fetchProfile({ userId: user.userId });
        }

        if (profile) {
          // Filter learning languages
          const languages = profile.languages?.filter((lang) => lang.learning) || [];
          setLearningLanguages(languages);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user?.userId]);

  if (!user || isLoading) return null;

  // Only show up to 3 language links
  const languageLinks = learningLanguages.slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
      <div className="flex flex-col gap-4 max-w-md w-full text-center">
        <div className="text-6xl mb-6">🎉</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">You're all set, {user.name}!</h1>
        <p className="text-lg text-gray-600 mb-8">
          Your personalized language learning journey is ready to begin. We've prepared everything based on your
          preferences.
        </p>

        {/* Set up availability - highlighted option */}
        <div className="mb-8">
          <Link
            to="/settings#availability"
            className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors focus:ring-4 focus:ring-blue-300 block"
          >
            Set up your availability
          </Link>
          <p className="text-sm text-gray-600 mt-2">
            Let others know when you're free to chat by setting specific time slots for language practice sessions
          </p>
        </div>

        {/* Language partner links - only display if user has learning languages */}
        {languageLinks.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Or connect with language partners:</p>
            {languageLinks.map((lang) => (
              <Link
                key={lang.languageCode}
                to={`/dashboard?languageCode=${lang.languageCode}&learning=true`}
                className="flex-1 py-3 px-4 bg-gray-50 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors focus:ring-4 focus:ring-blue-300 block"
              >
                Find {getLanguageName(lang.languageCode)} partners
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
